import React, { useState, useEffect, useMemo, useCallback } from 'react'
import Table from '../../components/Table'
import SimpleCard from '../../components/templates/SimpleCard'
import TextField from '../../components/Forms/TextField'
import DateField from '../../components/Forms/DateField'
import { getTransactionDataFromExcel } from '../../utils/excelReader'


const DataTransaksi = () => {
  const [allData, setAllData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [paginatedData, setPaginatedData] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    pengirim: '',
    penerima: '',
    startDate: null,
    endDate: null,
    page: 1,
    limit: 10,
    sort: 'tanggal|desc'
  })

  // Load data from Excel files
  useEffect(() => {
    setLoading(true)
    setTimeout(() => {
      const excelData = getTransactionDataFromExcel() // Get data from Excel files
      setAllData(excelData)
      setFilteredData(excelData)
      setLoading(false)
    }, 500)
  }, [])

  // Filter and sort data based on filters
  const applyFilters = useCallback(() => {
    let filtered = [...allData]

    // Filter by pengirim
    if (filters.pengirim) {
      filtered = filtered.filter(item => 
        item.namaPengirim.toLowerCase().includes(filters.pengirim.toLowerCase())
      )
    }

    // Filter by penerima
    if (filters.penerima) {
      filtered = filtered.filter(item => 
        item.namaPenerima.toLowerCase().includes(filters.penerima.toLowerCase())
      )
    }

    // Filter by date range
    if (filters.startDate || filters.endDate) {
      filtered = filtered.filter(item => {
        // Convert DD/MM/YYYY to Date object
        const [day, month, year] = item.tanggal.split('/')
        const itemDate = new Date(year, month - 1, day)
        
        if (filters.startDate && filters.endDate) {
          return itemDate >= filters.startDate && itemDate <= filters.endDate
        } else if (filters.startDate) {
          return itemDate >= filters.startDate
        } else if (filters.endDate) {
          return itemDate <= filters.endDate
        }
        return true
      })
    }

    // Apply sorting
    if (filters.sort) {
      const [sortField, sortDirection] = filters.sort.split('|')
      filtered.sort((a, b) => {
        let aValue = a[sortField]
        let bValue = b[sortField]

        // Handle date sorting
        if (sortField === 'tanggal') {
          const aParts = aValue.split('/')
          const bParts = bValue.split('/')
          if (aParts.length === 3 && bParts.length === 3) {
            const aDay = parseInt(aParts[0], 10)
            const aMonth = parseInt(aParts[1], 10) - 1
            const aYear = parseInt(aParts[2], 10)
            const bDay = parseInt(bParts[0], 10)
            const bMonth = parseInt(bParts[1], 10) - 1
            const bYear = parseInt(bParts[2], 10)
            aValue = new Date(aYear, aMonth, aDay)
            bValue = new Date(bYear, bMonth, bDay)
            // If invalid date, set to epoch
            if (isNaN(aValue.getTime())) aValue = new Date(0)
            if (isNaN(bValue.getTime())) bValue = new Date(0)
          } else {
            // If not valid format, treat as string
            aValue = aValue.toLowerCase()
            bValue = bValue.toLowerCase()
          }
        }

        // Handle numeric sorting
        if (sortField === 'nominal' || sortField === 'biaya') {
          aValue = Number(aValue) || 0
          bValue = Number(bValue) || 0
        }

        // Handle string sorting
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase()
          bValue = bValue.toLowerCase()
        }

        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
        }
      })
    }

    setFilteredData(filtered)
  }, [allData, filters])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  // Apply pagination to filtered data
  const applyPagination = useCallback(() => {
    const startIndex = (filters.page - 1) * filters.limit
    const endIndex = startIndex + filters.limit
    const paginated = filteredData.slice(startIndex, endIndex)
    setPaginatedData(paginated)
  }, [filteredData, filters.page, filters.limit])

  useEffect(() => {
    applyPagination()
  }, [applyPagination])

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' && key !== 'limit' ? 1 : prev.page // Reset to first page when filtering (except for page/limit changes)
    }))
  }

  // Handle page change
  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page: page
    }))
  }

  // Handle limit change
  const handleLimitChange = (limit) => {
    setFilters(prev => ({
      ...prev,
      limit: limit,
      page: 1 // Reset to first page when changing limit
    }))
  }

  // Table columns configuration
  const columns = useMemo(() => [
    {
      key: 'tanggal',
      title: 'Tanggal',
      align: 'left',
      sortable: true
    },
    {
      key: 'namaPengirim',
      title: 'Nama Pengirim',
      align: 'left',
      sortable: true
    },
    {
      key: 'namaPenerima',
      title: 'Nama Penerima',
      align: 'left',
      sortable: true
    },
    {
      key: 'keterangan',
      title: 'Keterangan',
      align: 'left',
      sortable: false
    },
    {
      key: 'tipe',
      title: 'Tipe',
      align: 'left',
      sortable: true
    },
    {
      key: 'nominal',
      title: 'Nominal',
      align: 'right',
      sortable: true,
      render: (value) => new Intl.NumberFormat('id-ID').format(value || 0)
    },
    {
      key: 'biaya',
      title: 'Biaya',
      align: 'right',
      sortable: true,
      render: (value) => new Intl.NumberFormat('id-ID').format(value || 0)
    }
  ], [])



  // Table configuration
  const tableConfig = {
    columns,
    withIndex: true,
    total: filteredData.length,
    limit: filters.limit,
    currentPage: filters.page,
    showRender: true,
    pagination: true,
    loading: loading,
    sort: filters.sort,
    collapse: false
  }



  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Loading...</h1>
        {/* <p className="page-subtitle">Data yang dibuat berdasarkan tanggal</p> */}
      </div>
      
      {/* <SimpleCard>
        <Table
          data={paginatedData}
          config={tableConfig}
          onChangePage={handlePageChange}
          onLimit={handleLimitChange}
          onSort={(sort) => handleFilterChange('sort', sort)}

        >
          <div className="row mb-12">
            <div className="col-md-3">
              <TextField
                variant="outlined"
                label="Filter Pengirim"
                placeholder="Nama pengirim..."
                value={filters.pengirim}
                onChange={(e) => handleFilterChange('pengirim', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <TextField
                variant="outlined"
                label="Filter Penerima"
                placeholder="Nama penerima..."
                value={filters.penerima}
                onChange={(e) => handleFilterChange('penerima', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <DateField
                variant="outlined"
                label="Tanggal Mulai"
                value={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
              />
            </div>
            <div className="col-md-3">
              <DateField
                variant="outlined"
                label="Tanggal Akhir"
                value={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
              />
            </div>
          </div>
        </Table>
      </SimpleCard> */}
    </div>
  )
}

export default DataTransaksi